import React from 'react';
import clsx from 'clsx';
import { ReactComponent as InfoIcon } from '../../assets/icons/info-icon.svg';
import { Button } from '../Button/Button';
import { KpiTableProps, KpiRow } from './types';
import styles from './KpiTableFactsDesktop.module.scss';

function formatValue(value: number | null): string {
  if (value === null) return '—';
  return value.toString();
}

const CURRENT_YEAR = 2026;

export function KpiTableFactsDesktop({
  rows,
  editable,
  onKpiClick,
  onEdit,
  onDelete,
  onMethodologyClick
}: KpiTableProps) {
  const years = rows[0]?.years.map((y) => y.year) ?? [];

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th className={styles.thNum} rowSpan={2}>
            №
          </th>
          <th className={styles.thName} rowSpan={2}>
            Название KPI
          </th>
          <th className={styles.thUnit} rowSpan={2}>
            Ед. изм.
          </th>
          {years.map((year) => (
            <React.Fragment key={year}>
              <th className={styles.thNum} rowSpan={2}>
                План {year}
              </th>
              {year === CURRENT_YEAR ? (
                <th className={clsx(styles.thNum, styles.current)} colSpan={3}>
                  Факт {year}
                </th>
              ) : (
                <th className={styles.thNum} rowSpan={2}>
                  Факт {year}
                </th>
              )}
            </React.Fragment>
          ))}
          <th className={styles.thNum} rowSpan={2}>
            Вес
          </th>
          <th className={styles.thMethod} rowSpan={2}>
            Методика расчёта
          </th>
          <th className={styles.thActions} />
        </tr>
        <tr>
          {years.map(
            (year) =>
              year === CURRENT_YEAR && (
                <React.Fragment key={`subheader-${year}`}>
                  <th className={styles.thNum}>6 мес</th>
                  <th className={styles.thNum}>9 мес</th>
                  <th className={styles.thNum}>12 мес</th>
                </React.Fragment>
              )
          )}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <KpiTableRow
            key={row.id}
            row={row}
            index={index}
            editable={editable}
            onKpiClick={onKpiClick}
            onEdit={onEdit}
            onDelete={onDelete}
            onMethodologyClick={onMethodologyClick}
          />
        ))}
      </tbody>
    </table>
  );
}

interface RowProps {
  row: KpiRow;
  index: number;
  editable?: boolean;
  onKpiClick?: (row: KpiRow) => void;
  onEdit?: (row: KpiRow) => void;
  onDelete?: (row: KpiRow) => void;
  onMethodologyClick?: (row: KpiRow) => void;
}

function KpiTableRow({
  row,
  index,
  editable,
  onKpiClick,
  onEdit,
  onDelete,
  onMethodologyClick
}: RowProps) {
  return (
    <tr className={styles.row}>
      <td className={styles.tdNum}>{index + 1}</td>
      <td className={styles.tdName}>
        <button className={styles.nameLink}>{row.name}</button>
      </td>
      <td className={clsx(styles.td, styles.tdUnit)}>{row.unit}</td>
      {row.years.map((y) => {
        const isCurrentYear = y.year === CURRENT_YEAR;
        return (
          <React.Fragment key={y.year}>
            <td className={styles.td}>{formatValue(y.plan)}</td>
            {isCurrentYear ? (
              <>
                <td className={styles.td}>
                  {formatValue(y.fact6months ?? null)}
                </td>
                <td className={styles.td}>
                  {formatValue(y.fact9months ?? null)}
                </td>
                <td className={styles.td}>
                  {formatValue(y.fact12months ?? null)}
                </td>
              </>
            ) : (
              <td className={styles.td}>{formatValue(y.fact)}</td>
            )}
          </React.Fragment>
        );
      })}
      <td className={styles.td}>{row.weight}</td>
      <td className={styles.td}>
        <button
          className={styles.methodLink}
          onClick={() => onMethodologyClick?.(row)}
        >
          Методика&nbsp; <InfoIcon className={styles.infoIcon} />
        </button>
      </td>
      {editable && (
        <td className={styles.tdActions}>
          <Button
            className={styles.actionBtn}
            onClick={() => onKpiClick?.(row)}
            aria-label="Редактировать"
            variant={'secondary'}
          >
            Оспорить
          </Button>
        </td>
      )}
    </tr>
  );
}
